import { get, isNil, pick } from 'lodash-es';

import { isValidRangeColorRule } from './is-valid-range-color-rule.js';
import { ColorRuleProps } from '../../core/types/color-rule.js';
import { getColorPalette } from '../../core/utils/get-color-palette.js';
import { getAccessor } from '../../core/utils/getAccessor.js';
import { getValidColor } from '../../core/utils/getValidColor.js';
import {
  buildValidator,
  Range,
  VALUE_MATCHERS,
} from '../../core/utils/validators.js';
import { NAME_ACCESSOR } from '../constants.js';
import type {
  InternalTreeMapNode,
  RangeLegendColorMap,
} from '../types/internal.js';
import { NameAccessor } from '../types/treemap.js';

const getRuleColor = (rule: ColorRuleProps, subject: string | number | Range) =>
  rule.colorPalette
    ? getColorPalette(`${JSON.stringify(subject)}`, 0, rule.colorPalette)
        .Default
    : getValidColor(rule.color ?? '');

const getSubject = (
  rule: ColorRuleProps,
  node: InternalTreeMapNode,
  defaultValueAccessor?: NameAccessor,
) => {
  const valueAccessor = rule.valueAccessor;

  if (!valueAccessor) {
    return defaultValueAccessor === NAME_ACCESSOR.Value
      ? node.value
      : node.name;
  }

  if (valueAccessor === NAME_ACCESSOR.Name) {
    return node.name;
  }

  if (valueAccessor === NAME_ACCESSOR.Value) {
    return node.value;
  }

  const accessor = getAccessor(valueAccessor);
  if (accessor) {
    const value = get(node, accessor);
    if (!isNil(value)) {
      return value;
    }
  }

  return null;
};

/**
 * Function to apply color rules to a node
 * @param node - InternalTreeMapNode
 * @param colorRules - raw color rules to apply
 * @param defaultValueAccessor - accessor to use when no valueAccessor is specified
 * @returns - color token
 */
export const buildColorRule = (
  node: InternalTreeMapNode,
  colorRules?: ColorRuleProps[],
  defaultValueAccessor?: NameAccessor,
) => {
  if (!colorRules || colorRules.length === 0) {
    return undefined;
  }

  const isValid = buildValidator(VALUE_MATCHERS);

  for (let index = colorRules.length - 1; index >= 0; index--) {
    const rule = colorRules[index];
    const subject = getSubject(rule, node, defaultValueAccessor);

    if (!isNil(subject) && isValid(subject, rule.comparator, rule.matchValue)) {
      return getRuleColor(rule, subject);
    }
  }

  return undefined;
};

export const buildColorRangeLegendRule = (
  legendColorMap: RangeLegendColorMap,
  colorRules?: ColorRuleProps[],
) => {
  if (!colorRules || colorRules.length === 0) {
    return undefined;
  }

  const colorRuleMap: RangeLegendColorMap = new Map();

  colorRules.forEach((rule) => {
    legendColorMap.forEach((_, key) => {
      const range = pick(key, ['from', 'to']);

      if (!isNil(range) && isValidRangeColorRule(range, rule)) {
        const color = getRuleColor(rule, range);

        colorRuleMap.set(key, {
          color: { Default: color },
        });
      }
    });
  });

  return {
    for: (range: Record<string, number>) => {
      return colorRuleMap.get(range);
    },
  };
};
